import "./popularBoxes.css";
import { Eye } from "lucide-react";
import { routes } from "../../../routes";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import BoxModel from "../../BoxModel/BoxModel";
import { icons } from "../../../utils/globalImages";
import { popularBoxes } from "../../../api/modules/b2droppie.class";

const PopularBoxes = () => {
  const navigate = useNavigate();
  const [cardData, setCardData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [overlayData, setOverlayData] = useState({});
  const [hoveredCards, setHoveredCards] = useState([]);
  const [hoveredRiskIndex, setHoveredRiskIndex] = useState(null);
  const isDarkMode = useSelector((state) => state.theme.darkMode);

  useEffect(() => {
    getPopularBoxes();
  }, []);

  const encryptData = (data) => {
    const base64EncodedData = window?.btoa(data?.toString());
    return base64EncodedData;
  };

  const handleProductClick = (product) => {
    const boxNameEncrypt = encryptData(product?.slug);
    navigate(`${routes.BOXDETAIL}/${boxNameEncrypt}`);
  };

  const handleEyeClick = (e, product) => {
    e.stopPropagation();
    setShowModal(true);
    setOverlayData(product);
  };

  const handleClick = () => {
    const boxNameEncrypt = encryptData(overlayData?.slug);
    navigate(`${routes.BOXDETAIL}/${boxNameEncrypt}`);
  };

  const getPopularBoxes = async () => {
    try {
      const response = await popularBoxes();
      setCardData(response?.data?.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleMouseIN = (cardIndex) => {
    setHoveredCards((prevHoveredCards) => [...prevHoveredCards, cardIndex]);
  };

  const handleMouseOUT = (cardIndex) => {
    setHoveredCards((prevHoveredCards) =>
      prevHoveredCards.filter((index) => index !== cardIndex),
    );
  };

  const getRiskLevelValue = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "easy":
        return 10;
      case "normal":
        return 30;
      case "mild":
        return 50;
      case "hard":
        return 70;
      case "extremely hard":
        return 90;
      default:
        return 10;
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "easy":
        return "#4ca52e";
      case "normal":
        return "#65d83e";
      case "mild":
        return "#ffc93f";
      case "hard":
        return "#f99100";
      case "extremely hard":
        return "#cb0003";
      default:
        return "#4ca52e";
    }
  };

  const getRiskSegmentIndex = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "easy":
        return 0;
      case "normal":
        return 1;
      case "mild":
        return 2;
      case "hard":
        return 3;
      case "extremely hard":
        return 4;
      default:
        return 0;
    }
  };

  const getRiskTooltipText = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "easy":
        return "Very Low";
      case "normal":
        return "Low";
      case "mild":
        return "Medium";
      case "hard":
        return "High";
      case "extremely hard":
        return "Extreme";
      default:
        return "";
    }
  };

  return (
    <>
    <div className="container">
      <div
        className={`pb-main-title ${isDarkMode ? "text-white" : "text-charcoal-gray"}`}
      >
        Popular Boxes
      </div>
      <div className={`pb-section-wrapper ${isDarkMode ? "dark" : "light"}`}>
        <div className="pb-cards-grid">
          {cardData?.length &&
            cardData?.map((product, index) => (
              <div
                key={index}
                className="pb-card-item"
                onMouseEnter={() => handleMouseIN(index)}
                onMouseLeave={() => handleMouseOUT(index)}
                onClick={() => handleProductClick(product)}
              >
                <div
                  className="position-absolute badges"
                  onClick={(e) => handleEyeClick(e, product)}
                >
                  <Eye
                    size={20}
                    style={{ cursor: "pointer", opacity: "0.2" }}
                    color={isDarkMode ? "#E8DEF8" : "#684199"}
                  />
                </div>
                {/* Product Image */}
                <img
                  alt={product?.name}
                  src={product?.box_images?.image}
                  className="pb-product-image"
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/180x110?text=No+Image";
                  }}
                />
                {/* Product Name */}
                <h6 className="pb-product-name">{product?.name}</h6>

                {/* Price Button */}
                <div className="pb-price-button">
                  {product?.formatted_box_price}
                </div>

                {/* Risk Bar */}
                <div className="pb-risk-wrapper">
                  <div className="pb-risk-bar">
                    {[
                      "pb-green",
                      "pb-light-green",
                      "pb-yellow",
                      "pb-orange",
                      "pb-red",
                    ].map((color, idx) => {
                      const isActive =
                        idx === getRiskSegmentIndex(product?.risk_level);
                      return (
                        <div
                          key={idx}
                          className={`pb-risk-segment ${color} ${
                            isActive ? "pb-active" : ""
                          }`}
                          onMouseEnter={() => {
                            if (isActive) setHoveredRiskIndex(index);
                          }}
                          onMouseLeave={() => setHoveredRiskIndex(null)}
                        />
                      );
                    })}
                  </div>
                  <div
                    className="pb-risk-indicator"
                    style={{
                      left: `${getRiskLevelValue(product?.risk_level)}%`,
                      "--pb-tooltip-bg": getRiskColor(product?.risk_level),
                    }}
                    onMouseEnter={() => setHoveredRiskIndex(index)}
                    onMouseLeave={() => setHoveredRiskIndex(null)}
                  >
                    <img
                      src={
                        isDarkMode
                          ? icons.riskIndicatorDark
                          : icons.riskIndicatorLight
                      }
                      style={{width:"10px",height:"10px"}}
                      alt="indicator"
                    />
                    <span
                      className={`pb-tooltip-box ${
                        hoveredRiskIndex === index ? "pb-tooltip-visible" : ""
                      }`}
                    >
                      {getRiskTooltipText(product?.risk_level)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
      {!cardData?.length && (
        <div className="w-100 d-center pb-4 text-muter-lavender-purple">
          <p>No products available in this box</p>
        </div>
      )}
      <BoxModel
        show={showModal}
        item={overlayData}
        onOpenBox={handleClick}
        isDarkMode={isDarkMode}
        onHide={() => setShowModal(false)}
      />
      </div>
    </>
  );
};

export default PopularBoxes;
